/* @HEADER
 * @version 1.1.963 | 2026-10-26
 * @purpose The state of the authenticated app as ONE hook: the auth adapter for this mount, the registry effects, the route and its hash overlay, the host navigation, the Spaces and their CRUD and delete flow, the live UI-mode subscriptions, the boot seed, the daily WIP snapshot, and the legacy-hash redirect — composed in the order App.tsx composed them, so App.tsx is assembly only (ZVX-DEV-6191, App.tsx-by-meaning S7).
 * @sidecar useAuthenticatedAppState.ts.header.md
 * @layer app | @hex adapter | @ctx app
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8061, ZVX-DEV-7676, ZVX-DEV-7685, ZVX-DEV-6191, ZVX-DEV-6015, ZVX-DEV-2745, ZVX-DEV-1454, ZVX-DEV-2616, ZVX-DEV-7443, ZVX-DEV-7775, ZVX-DEV-8278

import { useEffect, useMemo } from 'react'
import type { AuthPort } from '../auth/AuthContext'
import { createAuthAdapterForSession } from '../auth/chooseAuthAdapter'
import { getActiveData } from '../lib/db'
import { installIdentityScopedResetWiring } from '../lib/identityScopedState'
import type { BoardGroupRecord } from '../lib/db'
import { getAllBoards } from '../lib/db/boardData'
import { canDeleteBoardGroup } from '@modules/board/public-api.mjs'
import { useProjectRegistrySyncEffects } from './useProjectRegistrySyncEffects'
import { useMembershipLiveRefresh } from './useMembershipLiveRefresh'
import { useAuthRouteState } from './useAuthRouteState'
import { useHostNavigation, type HostNavigation } from './useHostNavigation'
import { captureMainScroll } from '../app/captureMainScroll'
import { useBoardGroups } from '../features/kanban/useBoardGroups'
import { useBoardChangeTell } from './useBoardChangeTell'
import { useNavLinkOpen, type NavLinkOpen } from '../app/useNavLinkOpen'
import { useActiveSpaceFollowsReplacement } from '../app/useActiveSpaceFollowsReplacement'
import { useHostSubscriptions, type HostSubscriptions } from './useHostSubscriptions'
import { useProjectBootSeed } from './useProjectBootSeed'
import { useDailyWipSnapshotOnSpace } from './useDailyWipSnapshotOnSpace'
import { useSpaceCrud } from '../features/kanban/useSpaceCrud'
import { useSpaceDeleteFlow, type SpaceDeleteFlow } from '../app/useSpaceDeleteFlow'
import {
  CANONICAL_WORKSPACE_SETTINGS_HASH,
  isLegacyWorkspaceSettingsHash,
  resolveHashOverlay,
  type HashOverlay,
} from '../app/hashOverlay'
import type { Route } from '../app/routes'

export interface AuthenticatedAppState {
  authAdapter: AuthPort
  route: Route
  /** The full hash: the overlays and the route screen are decided from it. */
  authedHash: string
  hashOverlay: HashOverlay | null
  nav: HostNavigation
  boardGroups: BoardGroupRecord[]
  subscriptions: HostSubscriptions
  handleCreateBoardGroup: ReturnType<typeof useSpaceCrud>['handleCreateBoardGroup']
  handleUpdateBoardGroup: ReturnType<typeof useSpaceCrud>['handleUpdateBoardGroup']
  spaceDelete: SpaceDeleteFlow
  /** ZVX-DEV-8265 -- what a link in the address is doing: the notice to show while it has not opened, and its two buttons. */
  navLink: NavLinkOpen
}

/**
 * The order is the one App.tsx had, and it matters twice: the registry
 * effects take the adapter of THIS mount (ZVX-DEV-5935), and the delete flow
 * clears the active Space when the deleted one was on screen. The hash
 * redirect (ZVX-DEV-2616: an old `#workspace-settings` bookmark converges on
 * `#organization`) is the one effect that stayed in App.tsx after S3; it is
 * state, not assembly, so it lives here.
 */
export function useAuthenticatedAppState(onReady?: () => void): AuthenticatedAppState {
  // Wave 1.A → Wave 5 BBA seam (ZVX-DEV-1106, ZVX-DEV-1349): decided once per mount (S1).
  const authAdapter = useMemo<AuthPort>(
    () => createAuthAdapterForSession({ userListSource: () => getActiveData().then((d) => d?.users ?? []) }),
    [],
  )
  // ZVX-DEV-5935 -- the once-per-session registry-sync effects, exactly where they stood.
  useProjectRegistrySyncEffects(authAdapter)
  // ZVX-DEV-8061 -- the membership ask that ran once at load now repeats while the tab is seen, so an organisation or a role that changed reaches the open app.
  useMembershipLiveRefresh(authAdapter)
  // ZVX-DEV-7443 -- the identity-scoped resets run on THIS mount's adapter (a logout, the sign-out wipe, a login as someone else); torn down with the mount.
  useEffect(() => installIdentityScopedResetWiring({ adapter: authAdapter, win: window }), [authAdapter])
  // ZVX-DEV-1454 -- the URL hash is the source of truth for the authenticated route.
  const { route, hash: authedHash } = useAuthRouteState()
  const nav = useHostNavigation({ route, captureScroll: captureMainScroll })
  const groupsApi = useBoardGroups()
  // ZVX-DEV-7676 -- the recorder is told once for every space entered, at the one place that watches the change.
  useBoardChangeTell({ activeSpaceId: nav.activeSpaceId, boardGroups: groupsApi.groups, takeNavVia: nav.takeNavVia })
  const subscriptions = useHostSubscriptions({
    refreshGroups: groupsApi.refresh,
    setActiveSpaceId: nav.setActiveSpaceId,
    setActiveView: nav.setActiveView,
    setSearchHighlightOnOpen: nav.setSearchHighlightOnOpen,
    markQuietArrival: nav.markQuietArrival,
  })
  useProjectBootSeed({ refreshGroups: groupsApi.refresh, onReady })
  useDailyWipSnapshotOnSpace(nav.activeSpaceId)
  const { handleCreateBoardGroup, handleUpdateBoardGroup } = useSpaceCrud({
    boardGroups: groupsApi.groups,
    saveBoardGroup: groupsApi.save,
  })
  // ZVX-DEV-5637 -- the space-delete question; the Space on screen is cleared when it is the one deleted.
  const spaceDelete = useSpaceDeleteFlow({
    boardGroups: groupsApi.groups,
    canDeleteBoardGroup: canDeleteBoardGroup as (
      groupId: string,
      groups: BoardGroupRecord[],
    ) => { valid: boolean; error?: string },
    getAllBoards,
    removeGroup: groupsApi.remove,
    onDeleted: (groupId) => {
      if (nav.activeSpaceId === groupId) nav.setActiveSpaceId(null)
    },
  })

  // ZVX-DEV-8265 -- a link in the address (#tasks?p=&s=&b=) is opened only for a person who may see it now.
  const navLink = useNavLinkOpen({
    hash: authedHash,
    spaces: groupsApi.groups,
    spacesLoading: groupsApi.loading,
    nav,
  })

  // ZVX-DEV-8355 -- the reconcile may drop the row the person stands in; the space on screen moves to the row of the same server space.
  useActiveSpaceFollowsReplacement({
    groups: groupsApi.groups,
    loading: groupsApi.loading,
    activeSpaceId: nav.activeSpaceId,
    setActiveSpaceId: nav.setActiveSpaceId,
  })

  const hashOverlay = resolveHashOverlay(authedHash)
  const legacyHash = isLegacyWorkspaceSettingsHash(authedHash)
  useEffect(() => {
    if (legacyHash && typeof window !== 'undefined') {
      window.location.hash = CANONICAL_WORKSPACE_SETTINGS_HASH
    }
  }, [legacyHash])

  return {
    authAdapter,
    route,
    authedHash,
    hashOverlay,
    nav,
    boardGroups: groupsApi.groups,
    subscriptions,
    handleCreateBoardGroup,
    handleUpdateBoardGroup,
    spaceDelete,
    navLink,
  }
}
